import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '@jmvilaca-corp/auth-ui'

/**
 * Service worker des notifications. L'application le sert a sa racine
 * (`public/push-sw.js`) : un service worker ne voit que ce qui est sous son
 * propre chemin.
 */
export const pushWorkerPath = '/push-sw.js'

export type PushSupport = 'supported' | 'needs-install' | 'unsupported'

export function pushSupport(): PushSupport {
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) {
    return 'supported'
  }

  // iPhone et iPad : les notifications n'existent que pour l'application
  // ajoutee a l'ecran d'accueil, pas dans Safari. Un iPad se presente comme un Mac.
  const apple =
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)

  return apple ? 'needs-install' : 'unsupported'
}

const settingsKey = ['push', 'settings'] as const
const deviceKey = ['push', 'device'] as const

/** `publicKey` : `null` tant que le serveur n'a pas de cle, les notifications sont alors indisponibles. */
export function usePushSettings() {
  return useQuery({
    queryKey: settingsKey,
    queryFn: () => apiGet<{ publicKey: string | null }>('/api/profile/push'),
    staleTime: Infinity,
  })
}

function toBase64Url(buffer: ArrayBuffer | null): string {
  return buffer === null
    ? ''
    : btoa(String.fromCharCode(...new Uint8Array(buffer)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
}

function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'))
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

function send(subscription: PushSubscription) {
  const { endpoint, keys } = subscription.toJSON()
  return apiSend<null>('/api/profile/push/subscribe', 'POST', { endpoint, p256dh: keys?.p256dh, auth: keys?.auth })
}

/**
 * L'abonnement de cet appareil, s'il en a un : c'est le navigateur qui le sait.
 * Il est renvoye au serveur a chaque lecture, pour qu'un abonnement oublie
 * la-bas (ou passe a un autre compte) redevienne celui de l'utilisateur.
 */
export function usePushDevice(publicKey: string | null | undefined) {
  return useQuery({
    queryKey: [...deviceKey, publicKey ?? null] as const,
    enabled: typeof publicKey === 'string' && pushSupport() === 'supported',
    staleTime: Infinity,
    queryFn: async (): Promise<boolean> => {
      const registration = await navigator.serviceWorker.getRegistration(pushWorkerPath)
      const subscription = (await registration?.pushManager.getSubscription()) ?? null
      if (subscription === null) {
        return false
      }

      // Pris avec une autre cle du serveur : plus rien n'y arriverait.
      if (toBase64Url(subscription.options?.applicationServerKey ?? null) !== publicKey) {
        await subscription.unsubscribe()
        return false
      }

      await send(subscription)
      return true
    },
  })
}

/** A appeler depuis un clic : le navigateur ne demande l'accord de l'utilisateur que sur un geste. */
export function useEnablePush() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (publicKey: string) => {
      if ((await Notification.requestPermission()) !== 'granted') {
        throw new Error(
          "Les notifications sont refusees pour cette application. Autorisez-les dans les reglages de l'appareil, puis reessayez.",
        )
      }

      const registration = await navigator.serviceWorker.register(pushWorkerPath)
      await navigator.serviceWorker.ready

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: fromBase64Url(publicKey),
      })
      await send(subscription)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deviceKey }),
  })
}

export function useDisablePush() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const registration = await navigator.serviceWorker.getRegistration(pushWorkerPath)
      const subscription = (await registration?.pushManager.getSubscription()) ?? null
      if (subscription === null) {
        return
      }

      await apiSend<null>('/api/profile/push/unsubscribe', 'POST', { endpoint: subscription.endpoint })
      await subscription.unsubscribe()
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deviceKey }),
  })
}

/** Envoie une notification d'essai a tous les appareils de l'utilisateur ; rend le nombre d'appareils prevenus. */
export function useTestPush() {
  return useMutation({
    mutationFn: async () => (await apiSend<{ delivered: number }>('/api/profile/push/test', 'POST')).delivered,
  })
}
